/**
 * Category Slider controller.
 * Handles paging + swipe for the desktop track (5 collections/page) and the
 * mobile track (2 collections/page) independently. Hover-expand is pure CSS —
 * this file only ever moves the track between pages.
 */
class CategoryPager {
  constructor(root, breakpoint) {
    this.root = root;
    this.breakpoint = breakpoint; // 'desktop' or 'mobile'
    this.viewport = root.querySelector(`[data-cs-viewport="${breakpoint}"]`);
    this.track = root.querySelector(`[data-cs-track="${breakpoint}"]`);
    this.dotsWrap = root.querySelector(`[data-cs-dots="${breakpoint}"]`);
    this.dots = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('.category-slider__dot')) : [];
    this.pages = this.track ? Array.from(this.track.children) : [];
    this.pageCount = this.pages.length;

    this.index = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.viewportWidth = 0;

    if (!this.track || this.pageCount <= 1) return;

    this.bindEvents();
    this.goTo(0, false);
  }

  bindEvents() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.csGoto, 10)));
    });

    this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.track.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));
    this.track.addEventListener('pointerleave', this.onPointerLeave.bind(this));

    window.addEventListener('resize', () => this.goTo(this.index, false));
  }

  onPointerDown(e) {
    // Ignore drags that start on a link so a simple tap still navigates.
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragDeltaX = 0;
    this.viewportWidth = this.viewport.getBoundingClientRect().width;
    this.track.style.transition = 'none';
    try { this.track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
  }

  onPointerMove(e) {
    if (!this.isDragging) return;
    this.dragDeltaX = e.clientX - this.dragStartX;
    if (Math.abs(this.dragDeltaX) > 6) e.preventDefault();
    const base = -this.index * this.viewportWidth;
    this.track.style.transform = `translateX(${base + this.dragDeltaX}px)`;
  }

  onPointerUp(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.transition = '';

    const threshold = this.viewportWidth * 0.15;
    if (this.dragDeltaX > threshold) {
      this.goTo(this.index - 1);
    } else if (this.dragDeltaX < -threshold) {
      this.goTo(this.index + 1);
    } else {
      this.goTo(this.index, true);
      // A negligible drag is treated as a tap — let the click on the link proceed.
    }
  }

  onPointerLeave() {
    if (this.isDragging) this.onPointerUp();
  }

  goTo(index, animate = true) {
    if (index < 0) index = this.pageCount - 1;
    if (index >= this.pageCount) index = 0;
    this.index = index;

    this.track.style.transition = animate ? 'transform 0.45s ease' : 'none';
    this.track.style.transform = `translateX(-${index * 100}%)`;

    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }
}

function initCategorySliders(scope = document) {
  scope.querySelectorAll('[data-category-slider]').forEach((root) => {
    if (root.dataset.csInitialized) return;
    root.dataset.csInitialized = 'true';
    new CategoryPager(root, 'desktop');
    new CategoryPager(root, 'mobile');
  });
}

document.addEventListener('DOMContentLoaded', () => initCategorySliders());
document.addEventListener('shopify:section:load', (e) => initCategorySliders(e.target));
